import * as React from 'react';
import ReactModal, { Classes } from 'react-modal';
import styled, { css } from 'styled-components';
import mediaQueries from '@Utils/shared/constants/mediaQueries';
import ScrollContainer from '@Utils/shared/components/ScrollContainer';
import CrossThin from '@Utils/shared/components/Icons-v3/CrossThin';

interface ReactModalProps {
  isOpen: boolean;
  onRequestClose?: () => void;
  className?: string;
  onAfterOpen?: () => void;
  onAfterClose?: () => void;
  modalClassName?: Classes;
  adaptiveModal?: boolean;
  shouldCloseOnOverlayClick?: boolean;
  closeTimeoutMS?: number;
}

interface StyledModalProps {
  isBgColor?: boolean;
  fadeOut?: boolean;
  isHeaderAbove?: boolean;
  bgImage?: string;
  borderRValue?: number;
  topPosition?: number;
  ariaHideApp?: boolean;
}

const ReactModalAdapter: ReactFCWithChildren<ReactModalProps> = ({
  className,
  modalClassName,
  ...props
}) => <ReactModal className={modalClassName} portalClassName={className} {...props} />;

export const Header = styled.header`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 0;

  @media ${mediaQueries.desktop} {
    padding: 20px 0 0;
  }
`;

export const Modal = styled(ReactModalAdapter).attrs({
  overlayClassName: {
    base: 'Overlay',
    afterOpen: 'OverlayAfter',
    beforeClose: 'OverlayBefore',
  },
  modalClassName: {
    base: 'Modal',
    afterOpen: 'ModalAfter',
    beforeClose: 'ModalBefore',
  },
})<StyledModalProps>`
  .Overlay {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: ${(props) => (props.isHeaderAbove ? '5001' : '9999')};
    position: fixed;
    opacity: 0;
    transition: opacity 0.3s;

    ${(props) =>
      props.bgImage &&
      css`
        &::after,
        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
        }

        &::after {
          z-index: 0;
          background-image: url(${props.bgImage});
          background-size: cover;
          background-position: 50% 50%;
        }

        &::before {
          z-index: 1;
          backdrop-filter: blur(4px);
          background-color: rgba(216, 216, 216, 0.15);
        }
      `}

    @media ${mediaQueries.desktop} {
      overflow-y: auto;
      padding: 20px;
      display: flex;
      align-items: flex-start;
    }
  }

  .OverlayAfter {
    opacity: 1;
  }

  .OverlayBefore {
    opacity: 0;
  }

  .Modal {
    position: relative;
    z-index: 2;
    margin: ${(props) =>
      props.topPosition ? `${props.topPosition}vh auto 20px` : '20vh auto 20px'};
    background: ${(props) => props.isBgColor && props.theme.color.semidark};
    border-radius: ${(props) => (props.borderRValue ? `${props.borderRValue}px` : '4px')};
    overflow: hidden;
    transition: opacity 0.3s, transform 0.3s;
    transform: translateY(20px);

    ${(props) =>
      props.adaptiveModal &&
      css`
        ${Header} {
          background-color: ${(props) => props.theme.color.superdark};
        }
        @media ${mediaQueries.tablet} {
          top: 0;
          right: 0;
          display: flex;
          flex-direction: column;
          transform: initial;
          height: 100%;
          width: 370px;
          position: absolute;
        }

        @media ${mediaQueries.desktop} {
          height: initial;
          width: fit-content;
          border-radius: 8px;
          box-shadow: 0 40px 60px -15px rgba(0, 0, 0, 0.88);
          background-color: #2b2c30;

          ${Header} {
            background-color: ${(props) => props.theme.color.regular};
          }
        }
      `}
  }

  .ModalAfter {
    transform: translateY(0);
  }

  .ModalBefore {
    transform: translateY(20px);
  }

  &.form-overlay {
    @media ${mediaQueries.mobileOrTablet} {
      .Overlay {
        top: 60px;
      }

      .Modal {
        border-radius: 0;
      }
    }
  }

  &.privacy-agreement-overlay {
    .Overlay {
      z-index: 10000;
    }
  }
`;

export const Title = styled.span`
  font-size: 15px;
  font-weight: 600;
  cursor: default;
  color: ${(props) => props.theme.color.superBright};
  text-transform: uppercase;
  line-height: 25px;
`;

export const CloseButton = styled.button`
  cursor: pointer;
  position: absolute;
  right: 13px;
  width: 20px;
  height: 20px;
  align-self: center;
  border: 0;
  background: none;

  @media ${mediaQueries.desktop} {
    right: 16px;
  }
`;

export const CrossIcon = styled(CrossThin)`
  width: 20px;
  height: 20px;
`;

const ContentCss = css`
  display: flex;
  flex-direction: column;
  margin: 20px 0;
  padding: 0 20px;
  flex-grow: 1;
`;

export const Content = styled.div`
  ${ContentCss};
  overflow: hidden;
`;

export const ContentWithScroll = styled(ScrollContainer)`
  ${ContentCss};
`;

export const Footer = styled.footer`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: auto 20px 20px;
`;
